// Order Details "Readiness Checklist" — the section-status model (ticket 86d385wc0).
//
// THE SPINE. One pure derivation from the draft + linked FO that powers card
// badges, (Phase 2) the readiness bar, and (Phase 2) submit gating. Keeping it a
// single source of truth is what guarantees a card can never read "ok" while
// submit says "no".
//
// Required-set mirrors NetSuite's asterisked fields — DOP submit is ≥ as strict
// as the NS UI. This MUST stay in lock-step with
// CSC_CORE/Features/SalesOrders/Validators/SubmitSalesOrderRequestDtoValidator.cs
// (resolved decision, ClickUp 86d385wc0 — required: customerPoNumber, poExpiry,
// deliveryDue, officeContact1 + contactNumber1, state/province, city/town,
// shipping address, waivedFreight, ≥1 matched line item, linkedFO).

import type { SalesOrderDto } from '../../types/salesOrder.types';

export type SectionStatusKind = 'complete' | 'attention' | 'optional' | 'locked';

export type SectionKey =
  | 'inherited'
  | 'pricing'
  | 'purchaseOrder'
  | 'shipping'
  | 'delivery'
  | 'flags';

export interface SectionDescriptor {
  key: SectionKey;
  title: string;
  kind: SectionStatusKind;
  /** One-line collapsed summary shown when the card is closed. */
  summary: string;
  /** Required-but-missing field labels — fuel for the Phase-2 readiness bar. */
  blockers: string[];
}

// Subset of the draft the model reads. Accepting a partial keeps the model
// trivially testable and decoupled from the full DTO surface.
type DraftSlice = Pick<
  SalesOrderDto,
  | 'customerName'
  | 'doCustCompanyName'
  | 'termsName'
  | 'projectName'
  | 'overallPricePerKg'
  | 'customerPoNumber'
  | 'poExpiryDate'
  | 'subPoOrderlistLink'
  | 'invoiceToLetter'
  | 'shippingAddressId'
  | 'shippingAddresseeName'
  | 'shippingAddrLine1'
  | 'shippingStateProvinceId'
  | 'shippingCityTownId'
  | 'waivedFreight'
  | 'deliveryDueDate'
  | 'scheduleId'
  | 'officeContact1'
  | 'contactNumber1'
  | 'authorizedReceivers'
  | 'otherInstructions'
  | 'soRemarks'
  | 'isTrailer'
  | 'isMf'
  | 'isPpeRequired'
  | 'isPermitRequired'
  | 'isPriority'
  | 'isSpotOrder'
  | 'isReprint'
>;

function isBlank(v: string | null | undefined): boolean {
  return v == null || v.trim() === '';
}

// Mirrors the shippingValue adapter in SalesOrderDetailPanel: a saved address
// ref OR a typed custom address both count as "present".
function hasShippingAddress(d: DraftSlice): boolean {
  return Boolean(d.shippingAddressId || d.shippingAddrLine1 || d.shippingAddresseeName);
}

// Resolution order: locked wins, then any blocker → attention, then a section
// that owns required fields and has them all → complete, else optional.
function resolveKind(
  locked: boolean,
  blockers: string[],
  ownsRequired: boolean,
): SectionStatusKind {
  if (locked) return 'locked';
  if (blockers.length > 0) return 'attention';
  if (ownsRequired) return 'complete';
  return 'optional';
}

function joinSummary(parts: Array<string | null | undefined>): string {
  return parts.filter((p): p is string => Boolean(p && p.trim())).join(' · ');
}

/**
 * Derive the ordered section descriptors for the Order Details checklist.
 * `foPicked` distinguishes "no FO yet" (locked cards read empty) from a live FO.
 */
export function deriveSectionStatuses(
  draft: DraftSlice,
  foPicked: boolean,
): SectionDescriptor[] {
  // § Customer & Contract — mostly trusted carry-over, but Customer/Terms/Project are
  // editable. Project is coordinator-required at submit (86d3f81cz) — gate on it in
  // lock-step with SubmitSalesOrderRequestDtoValidator. Only enforced once an FO is picked
  // (before that the section reads empty/optional).
  const contractBlockers: string[] = [];
  if (foPicked && isBlank(draft.projectName)) contractBlockers.push('Project');
  const inherited: SectionDescriptor = {
    key: 'inherited',
    title: 'Customer & Contract',
    kind: foPicked ? resolveKind(false, contractBlockers, true) : 'optional',
    summary: foPicked
      ? joinSummary([draft.doCustCompanyName || draft.customerName, draft.termsName]) ||
        'Customer & contract details'
      : 'Pick a Firm Offer to set customer & contract',
    blockers: contractBlockers,
  };

  // § Pricing — locked, FO-rate only (no client-side totals; NetSuite computes
  // those on submit — resolved decision, Must-Fix #2).
  const pricing: SectionDescriptor = {
    key: 'pricing',
    title: 'Pricing',
    kind: 'locked',
    summary:
      draft.overallPricePerKg != null
        ? `₱${draft.overallPricePerKg.toFixed(2)}/kg overall · FO-locked`
        : 'FO-locked pricing',
    blockers: [],
  };

  // § Purchase Order & Documents — required: Customer PO #, PO Expiry. Customer
  // PO # is now coordinator-entered + required (86d39w9rn) — the "Waiting for
  // Official PO" flag is informational and does NOT relax it (a provisional PO #
  // is still entered). Must stay in lock-step with SubmitSalesOrderRequestDtoValidator.
  const poBlockers: string[] = [];
  if (isBlank(draft.customerPoNumber)) poBlockers.push('Customer PO #');
  if (isBlank(draft.poExpiryDate)) poBlockers.push('PO Expiry Date');
  const purchaseOrder: SectionDescriptor = {
    key: 'purchaseOrder',
    title: 'Purchase Order & Documents',
    kind: resolveKind(false, poBlockers, true),
    summary: joinSummary([
      isBlank(draft.customerPoNumber) ? 'No customer PO #' : `PO ${draft.customerPoNumber}`,
      isBlank(draft.poExpiryDate) ? null : `expires ${draft.poExpiryDate}`,
    ]),
    blockers: poBlockers,
  };

  // § Shipping — required: State/Province, City/Town, Shipping Address, Waived
  // Freight. (Billing automation arrives in ticket 86d386e18.)
  const shipBlockers: string[] = [];
  if (isBlank(draft.shippingStateProvinceId)) shipBlockers.push('State / Province');
  if (isBlank(draft.shippingCityTownId)) shipBlockers.push('City / Town');
  if (!hasShippingAddress(draft)) shipBlockers.push('Shipping Address');
  if (isBlank(draft.waivedFreight)) shipBlockers.push('Waived Freight');
  const shipping: SectionDescriptor = {
    key: 'shipping',
    title: 'Shipping & Billing',
    kind: resolveKind(false, shipBlockers, true),
    summary: hasShippingAddress(draft)
      ? draft.shippingAddresseeName || draft.shippingAddrLine1 || 'Shipping address set'
      : 'No shipping address',
    blockers: shipBlockers,
  };

  // § Delivery Requirements — required: Delivery Due Date, Office Contact 1 +
  // Contact Number 1. (Schedule folded in from 86d385rzb.)
  const deliveryBlockers: string[] = [];
  if (isBlank(draft.deliveryDueDate)) deliveryBlockers.push('Delivery Due Date');
  if (isBlank(draft.officeContact1)) deliveryBlockers.push('Office Contact 1');
  if (isBlank(draft.contactNumber1)) deliveryBlockers.push('Contact Number 1');
  const delivery: SectionDescriptor = {
    key: 'delivery',
    title: 'Delivery Requirements',
    kind: resolveKind(false, deliveryBlockers, true),
    summary: joinSummary([
      isBlank(draft.deliveryDueDate) ? 'No delivery date' : `Due ${draft.deliveryDueDate}`,
      isBlank(draft.officeContact1) ? null : draft.officeContact1,
    ]),
    blockers: deliveryBlockers,
  };

  // § Flags & Notes — all optional; never blocks submit.
  const enabledFlags = [
    draft.isTrailer && 'Trailer',
    draft.isMf && 'M-F',
    draft.isPpeRequired && 'PPE',
    draft.isPermitRequired && 'Permit',
    draft.isPriority && 'Priority',
    draft.isSpotOrder && 'Spot',
    draft.isReprint && 'Reprint',
  ].filter((f): f is string => Boolean(f));
  const flags: SectionDescriptor = {
    key: 'flags',
    title: 'Flags & Notes',
    kind: 'optional',
    summary:
      joinSummary([
        enabledFlags.length ? enabledFlags.join(', ') : null,
        isBlank(draft.soRemarks) ? null : 'has remarks',
      ]) || 'No flags set',
    blockers: [],
  };

  return [inherited, pricing, purchaseOrder, shipping, delivery, flags];
}

/** Total count of blockers across every section — the readiness-bar headline. */
export function countBlockers(sections: SectionDescriptor[]): number {
  return sections.reduce((sum, s) => sum + s.blockers.length, 0);
}
